import {
  z,
  ZodTypeAny,
  ZodObject,
  ZodOptional,
  ZodNullable,
  ZodArray,
  ZodEffects,
  ZodDefault,
  ZodDate,
  ZodTuple,
  ZodLazy,
  ZodLiteral,
  ZodNull,
} from 'zod';

/**
 * 주어진 Zod 스키마를 기반으로 "값이 아직 입력되거나 결정되기를 기다리는 상태"를 나타내는 초기값을 생성합니다.
 * 특별한 "빈" 값 (예: [], null)이 필요한 경우를 제외하고, 대부분의 타입에 대해 `undefined`를 반환합니다.
 * 이 함수는 스키마에 정의된 `.default()` 값을 의도적으로 사용하지 않습니다.
 *
 * @param schema 초기값을 생성할 Zod 스키마입니다.
 * @returns 스키마 구조에 따른 "값을 기다리는 상태"의 초기값입니다.
 */
export const createInitialEmptyValuesFromSchema = <T extends ZodTypeAny>(schema: T): z.infer<T> => {
  if (schema instanceof ZodOptional || schema instanceof ZodNullable) {
    return createInitialEmptyValuesFromSchema(schema.unwrap());
  }
  if (schema instanceof ZodLazy) {
    return createInitialEmptyValuesFromSchema(schema.schema);
  }
  if (schema instanceof ZodEffects) {
    if ('_def' in schema && 'schema' in schema._def && (schema._def.schema as ZodTypeAny) !== schema) {
      return createInitialEmptyValuesFromSchema(schema._def.schema as ZodTypeAny);
    }
  }
  if (schema instanceof ZodDefault) {
    return createInitialEmptyValuesFromSchema(schema.removeDefault()); // .default() 무시
  }

  if (schema instanceof ZodObject) {
    const shape = schema.shape;
    const initialObject: Record<string, unknown> = {};
    for (const key in shape) {
      if (Object.prototype.hasOwnProperty.call(shape, key)) {
        initialObject[key] = createInitialEmptyValuesFromSchema(shape[key] as ZodTypeAny);
      }
    }
    return initialObject as z.infer<T>;
  }
  if (schema instanceof ZodArray) {
    return [] as z.infer<T>; // 배열은 빈 배열 `[]`
  }
  if (schema instanceof ZodDate) {
    return null as z.infer<T>; // 날짜는 `null`
  }
  if (schema instanceof ZodTuple) {
    return schema.items.map((itemSchema: ZodTypeAny) =>
      createInitialEmptyValuesFromSchema(itemSchema),
    ) as z.infer<T>;
  }
  if (schema instanceof ZodLiteral) {
    return schema.value as z.infer<T>;
  }
  if (schema instanceof ZodNull) {
    return null as z.infer<T>; // ZodNull은 명시적으로 null
  }

  return undefined as z.infer<T>;
};
